/**
 * Users API Endpoints
 * Handles user profile data
 */

import { apiClient, getAuthHeaders } from "../client";
import type { RequestOptions, UserData } from "../types";

/**
 * Fetch the authenticated user's profile from the backend
 * @param userId - The Cognito user ID (sub) of the authenticated user
 * @returns User profile data
 */
export async function getUser(
  userId: string,
  options?: RequestOptions,
): Promise<{ success: true; data: UserData } | { success: false; error: Error }> {
  try {
    const authHeaders = await getAuthHeaders();
    const result = await apiClient.get<UserData>(`/users/${encodeURIComponent(userId)}`, {
      ...options,
      headers: {
        ...authHeaders,
        ...options?.headers,
      },
    });

    if (!result.success) {
      const errorMessage = result.error.message || "Failed to fetch user profile";
      return {
        success: false,
        error: new Error(errorMessage),
      };
    }

    return {
      success: true,
      data: result.data!,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error("Unknown error"),
    };
  }
}

export const usersApi = {
  getUser,
};
